import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { FileDrop } from '@/components/FileDrop';
import { Modal } from '@/components/Modal';
import { Badge, Card, ErrorNotice, Field, PageHeader, Spinner } from '@/components/ui';
import { useConfirm } from '@/components/ConfirmDialog';
import { useI18n } from '@/i18n';
import { MobileList } from '@/components/MobileList';
import { DataToolbar, NoResults, SortableTh } from '@/components/table';
import { api, type Document, type DocumentCategory, type DocumentVisibility } from '@/lib/api';
import { useTableState, type SortOption } from '@/lib/table';
import { dateShort, fileSize } from '@/lib/format';
import { useLabel } from '@/lib/useLabel';
import { Combobox } from '@/components/Combobox';
import { jobOptions } from '@/features/access/people';
import { DocumentViewer } from '@/features/documents/DocumentViewer';
import { RenameFileDialog } from '@/features/documents/RenameFileDialog';

const CATEGORIES = [
  '',
  'contract',
  'drawing',
  'risk_assessment',
  'certificate',
  'datasheet',
  'site_photo',
  'signed_sheet',
];

const DOC_SORTS: SortOption<Document>[] = [
  { key: 'uploaded', labelKey: 'documents.uploaded', value: (d) => d.uploadedAt, defaultDirection: 'desc' },
  { key: 'name', labelKey: 'documents.file', value: (d) => d.originalFilename },
  { key: 'category', labelKey: 'documents.category', value: (d) => d.category },
  { key: 'size', labelKey: 'documents.size', value: (d) => d.sizeBytes, defaultDirection: 'desc' },
];

export function DocumentsPage() {
  const { t } = useI18n();
  const confirm = useConfirm();
  const L = useLabel();
  const queryClient = useQueryClient();
  const [category, setCategory] = useState('');

  const [uploadOpen, setUploadOpen] = useState(false);
  // Defaults to the filter you are looking at: someone who filtered to "drawing" and
  // then uploaded is almost always uploading a drawing.
  const [uploadCategory, setUploadCategory] = useState<DocumentCategory>('other');
  const [uploadJobId, setUploadJobId] = useState('');
  const [uploadVisibility, setUploadVisibility] = useState<DocumentVisibility>('office_only');
  const [uploadLabel, setUploadLabel] = useState('');
  /** The document whose audience is being changed. */
  const [editing, setEditing] = useState<Document | null>(null);
  /** The document whose file is being renamed. */
  const [renaming, setRenaming] = useState<Document | null>(null);
  /** Position in the visible list of the document being previewed. */
  const [viewing, setViewing] = useState<number | null>(null);

  /** Only loaded when the upload dialog is open — the list is not needed otherwise. */
  const jobs = useQuery({ queryKey: ['jobs'], queryFn: () => api.jobs(), enabled: uploadOpen });

  const documents = useQuery({
    queryKey: ['documents', { category }],
    queryFn: () => api.documents({ category: category || undefined }),
  });

  const download = useMutation({
    mutationFn: async (id: string) => {
      // Every download is audited server-side before the URL is issued. If a contract
      // leaks, the first question is who opened it.
      const { url } = await api.downloadUrl(id);
      window.open(url, '_blank', 'noopener');
    },
  });

  const upload = useMutation({
    mutationFn: (file: File) =>
      api.uploadDocument(file, {
        category: uploadCategory,
        // No job and no person means a company document, which is most of what the
        // library actually holds.
        jobId: uploadJobId || undefined,
        visibility: uploadVisibility,
        customCategory:
          uploadCategory === 'other' && uploadLabel.trim() ? uploadLabel.trim() : undefined,
      }),
    onSuccess: async () => {
      setUploadOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });

  const changeVisibility = useMutation({
    mutationFn: (next: DocumentVisibility) =>
      api.updateDocument(editing!.id, { visibility: next }),
    onSuccess: async () => {
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteDocument(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['documents'] }),
  });

  // The uploader is searchable as well as the filename: "who uploaded the POS" is a
  // question the office asks more often than the filename it was saved under.
  const table = useTableState<Document>({
    rows: documents.data?.documents ?? [],
    search: (d) => [
      d.originalFilename,
      d.uploadedByName,
      d.customCategory,
      L('docCategory', d.category).label,
    ],
    sorts: DOC_SORTS,
    defaultSortKey: 'uploaded',
    storageKey: 'documents',
  });

  return (
    <>
      <PageHeader
        title={t('documents.title')}
        subtitle={t('documents.subtitle')}
        actions={
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              upload.reset();
              setUploadCategory((category || 'other') as DocumentCategory);
              setUploadJobId('');
              setUploadLabel('');
              // The safe default. Widening it is a decision; narrowing it after the
              // fact is a document that was briefly visible to the wrong people.
              setUploadVisibility('office_only');
              setUploadOpen(true);
            }}
          >
            {t('documents.addDocument')}
          </button>
        }
      />

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        size="sm"
        title={editing ? t('documents.changeVisibilityTitle', { name: editing.originalFilename }) : ''}
        description={t('documents.changeVisibilityHint')}
      >
        <div className="space-y-2">
          {(['office_only', 'assigned_engineers', 'all_staff'] as const).map((v) => (
            <button
              key={v}
              type="button"
              disabled={changeVisibility.isPending}
              onClick={() => changeVisibility.mutate(v)}
              className={`w-full text-start px-3 py-2.5 rounded border transition-colors ${
                editing?.visibility === v
                  ? 'border-[var(--color-primary)] bg-[var(--color-primary-soft)] text-primary font-semibold'
                  : 'border-border hover:bg-surface-alt'
              }`}
            >
              {v === 'office_only'
                ? t('common.onlyOffice')
                : v === 'all_staff'
                  ? t('common.allStaff')
                  : t('common.assignedEngineers')}
            </button>
          ))}

          {changeVisibility.error ? <ErrorNotice error={changeVisibility.error} /> : null}
        </div>
      </Modal>

      <Modal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        title={t('documents.addDocument')}
        description={t('documents.accepted')}
      >
        <div className="space-y-4">
          <Field label={t('documents.chooseCategory')}>
            <select
              className="input"
              value={uploadCategory}
              onChange={(e) => setUploadCategory(e.target.value as DocumentCategory)}
            >
              {CATEGORIES.filter(Boolean).map((c) => (
                <option key={c} value={c}>
                  {L('docCategory', c).label}
                </option>
              ))}
              <option value="other">{L('docCategory', 'other').label}</option>
            </select>
          </Field>

          {/* Only for "other" — a label on a row that already says "Certificate" is a
              second, contradictory answer to the same question. */}
          {uploadCategory === 'other' ? (
            <Field label={t('documents.customCategory')} hint={t('documents.customCategoryHint')}>
              <input
                className="input"
                maxLength={60}
                value={uploadLabel}
                placeholder={t('documents.customCategoryPlaceholder')}
                onChange={(e) => setUploadLabel(e.target.value)}
              />
            </Field>
          ) : null}

          <Field label={t('documents.visibility')} hint={t('documents.visibilityHint')}>
            <select
              className="input"
              value={uploadVisibility}
              onChange={(e) => setUploadVisibility(e.target.value as DocumentVisibility)}
            >
              <option value="office_only">{t('common.onlyOffice')}</option>
              <option value="assigned_engineers">{t('common.assignedEngineers')}</option>
              <option value="all_staff">{t('common.allStaff')}</option>
            </select>
          </Field>

          {/* Optional on purpose: a company policy belongs to nobody in particular. */}
          <Field label={t('common.job')} hint={t('common.optional')}>
            <Combobox
              value={uploadJobId}
              onChange={setUploadJobId}
              options={[{ value: '', label: t('common.none') }, ...jobOptions(jobs.data?.jobs ?? [])]}
            />
          </Field>

          <FileDrop onPick={(file) => upload.mutate(file)} busy={upload.isPending} />

          {upload.error ? <ErrorNotice error={upload.error} /> : null}
        </div>
      </Modal>

      <RenameFileDialog
        filename={renaming?.originalFilename ?? null}
        onClose={() => setRenaming(null)}
        onSave={async (filename) => {
          await api.updateDocument(renaming!.id, { filename });
          await queryClient.invalidateQueries({ queryKey: ['documents'] });
        }}
      />

      <DocumentViewer
        files={table.rows.map((d) => ({ id: d.id, filename: d.originalFilename, mimeType: d.mimeType }))}
        index={viewing}
        onIndex={setViewing}
        onClose={() => setViewing(null)}
        onDownload={(id) => download.mutate(id)}
      />

      <DataToolbar
        state={table}
        searchPlaceholder={t('documents.searchPlaceholder')}
        countOne="documents.countOne"
        countMany="documents.countMany"
        extra={
          <label className="flex items-center gap-2">
            <span className="sr-only">{t('documents.category')}</span>
            <select
              className="input w-auto"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c === '' ? t('common.all') : L('docCategory', c).label}
                </option>
              ))}
            </select>
          </label>
        }
      />

      {documents.isLoading ? (
        <Spinner />
      ) : documents.error ? (
        <ErrorNotice error={documents.error} />
      ) : (
        <Card padded={false}>
          <div className="lg:hidden p-2">
            <MobileList<Document>
              rows={table.rows}
              keyOf={(d) => d.id}
              title={(d) => d.originalFilename}
              subtitle={(d) => `${fileSize(d.sizeBytes)} \u00b7 ${dateShort(d.uploadedAt)}`}
              badges={(d) => (
                <Badge tone={L('docCategory', d.category).tone}>
                  {d.customCategory ?? L('docCategory', d.category).label}
                </Badge>
              )}
              fields={[
                {
                  label: t('documents.visibility'),
                  value: (d) =>
                    d.visibility === 'office_only'
                      ? t('common.onlyOffice')
                      : d.visibility === 'all_staff'
                        ? t('common.allStaff')
                        : t('common.assignedEngineers'),
                },
                { label: t('documents.uploaded'), value: (d) => d.uploadedByName },
                { label: t('documents.size'), value: (d) => fileSize(d.sizeBytes) },
              ]}
              action={(d) => (
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setViewing(table.rows.indexOf(d))}
                  >
                    {t('viewer.preview')}
                  </button>
                  <button type="button" className="btn-secondary" onClick={() => setRenaming(d)}>
                    {t('rename.short')}
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => download.mutate(d.id)}
                  >
                    {t('common.download')}
                  </button>
                </div>
              )}
            />
          </div>

          <div className="table-scroll max-lg:hidden">
            <table className="table-wide">
              <thead>
                <tr>
                  <SortableTh state={table} sortKey="name">
                    {t('documents.file')}
                  </SortableTh>
                  <SortableTh state={table} sortKey="category">
                    {t('documents.category')}
                  </SortableTh>
                  <th className="th">{t('documents.visibility')}</th>
                  <SortableTh state={table} sortKey="size">
                    {t('documents.size')}
                  </SortableTh>
                  <SortableTh state={table} sortKey="uploaded">
                    {t('documents.uploaded')}
                  </SortableTh>
                  <th className="th" />
                </tr>
              </thead>
              <tbody>
                {table.rows.map((d) => {
                  const cat = L('docCategory', d.category);
                  // Their word for it beats ours. "Other" tells a reader nothing.
                  const catLabel = d.customCategory ?? cat.label;
                  return (
                    <tr key={d.id} className="hover:bg-surface-alt">
                      <td className="td">
                        {/* The name opens the preview: looking is far more common than
                            saving, and every download used to land in a folder somewhere. */}
                        <button
                          type="button"
                          className="font-semibold text-start hover:text-primary hover:underline"
                          onClick={() => setViewing(table.rows.indexOf(d))}
                        >
                          {d.originalFilename}
                        </button>
                        {d.version > 1 ? (
                          <span className="text-xs text-muted ms-2 tabular">v{d.version}</span>
                        ) : null}
                      </td>
                      <td className="td">
                        <Badge tone={cat.tone}>{catLabel}</Badge>
                      </td>
                      <td className="td text-xs">
                        {/* The cell IS the control. Who can see a document is the thing
                            most likely to be wrong, and burying it behind an edit
                            dialog is how it stays wrong. */}
                        <button
                          type="button"
                          className="text-muted hover:text-primary hover:underline"
                          onClick={() => setEditing(d)}
                        >
                          {d.visibility === 'office_only'
                            ? t('common.onlyOffice')
                            : d.visibility === 'all_staff'
                              ? t('common.allStaff')
                              : t('common.assignedEngineers')}
                        </button>
                      </td>
                      <td className="td tabular text-muted whitespace-nowrap">{fileSize(d.sizeBytes)}</td>
                      <td className="td text-muted text-sm whitespace-nowrap">
                        {dateShort(d.uploadedAt)}
                        {d.uploadedByName ? (
                          <span className="block text-xs text-subtle">{d.uploadedByName}</span>
                        ) : null}
                      </td>
                      <td className="td text-end whitespace-nowrap">
                        <button
                          type="button"
                          className="td-action text-primary hover:underline"
                          onClick={() => download.mutate(d.id)}
                        >
                          {t('common.download')}
                        </button>
                        <button
                          type="button"
                          className="td-action text-muted hover:text-primary hover:underline ms-3"
                          onClick={() => setRenaming(d)}
                        >
                          {t('rename.short')}
                        </button>
                        <button
                          type="button"
                          className="td-action-danger ms-3"
                          onClick={async () => {
                            if (
                              await confirm({
                                message: t('documents.deleteConfirm', { name: d.originalFilename }),
                                confirmLabel: t('common.delete'),
                                danger: true,
                              })
                            ) {
                              remove.mutate(d.id);
                            }
                          }}
                        >
                          {t('common.delete')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {table.rows.length === 0 ? <NoResults onClear={table.clear} /> : null}
        </Card>
      )}
    </>
  );
}
